import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Typography, Box, Chip, ToggleButton, ToggleButtonGroup, IconButton
} from "@mui/material";
import { Add, ContentCopy, Delete } from "@mui/icons-material";
import httpClient from "../../_util/api";

const DURATIONS = [
  { label: "One time", value: 0 },
  { label: "12 hours", value: 12 },
  { label: "24 hours", value: 24 },
  { label: "36 hours", value: 36 },
  { label: "48 hours", value: 48 },
];

const AUDIENCES = [
  { label: "New Applicant", value: "new" },
  { label: "Existing Student", value: "returning" },
];

const STATUS_COLOR = {
  active: "success",
  used: "default",
  expired: "error",
};

// Original validity window in whole hours, recovered from created/expires
// timestamps (the list response doesn't carry the raw "hours" value).
const inviteHours = (invite) => {
  if (!invite.expiresAt || !invite.createdAt) return null;
  return Math.round((new Date(invite.expiresAt) - new Date(invite.createdAt)) / 3600000);
};

const inviteType = (invite) => {
  const h = inviteHours(invite);
  return h == null ? "One-time" : `${h} hours`;
};

const ScholarshipInviteLinks = () => {
  const [invites, setInvites] = useState([]);
  const [hours, setHours] = useState(24);
  const [audience, setAudience] = useState("new");
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [newLink, setNewLink] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const getInvites = async () => {
    try {
      const res = await httpClient.get("/students/listScholarshipInvites");
      if (res.status === 200) {
        setInvites(res.data);
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Failed to load invite links");
    }
  };

  useEffect(() => {
    getInvites();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    setNewLink(null);
    try {
      const res = await httpClient.post("/students/generateScholarshipInvite", { hours, audience });
      if (res.status === 201) {
        setNewLink(res.data.link);
        getInvites();
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Failed to generate invite link");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (link, id) => {
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1500);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this link? It will stop working immediately and won't be reusable.")) {
      return;
    }
    setDeletingId(id);
    setError("");
    try {
      await httpClient.delete(`/students/deleteScholarshipInvite/${id}`);
      setInvites((prev) => prev.filter((invite) => invite._id !== id));
    } catch (error) {
      setError(error?.response?.data?.message || "Failed to delete invite link");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        Scholarship Application Links
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
        Generate a link to the scholarship application form — for a new applicant, or for an existing student re-applying from their dashboard. A timed link (12–48 hours) can be used by many applicants and stays valid until it expires by time. A "One time" link has no time limit but works for a single applicant — it expires as soon as one application is submitted.
      </Typography>

      <Paper sx={{ width: "100%", overflow: "hidden", padding: 2, marginTop: "16px" }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <ToggleButtonGroup
            value={audience}
            exclusive
            onChange={(e, value) => value !== null && setAudience(value)}
            size="small"
          >
            {AUDIENCES.map((a) => (
              <ToggleButton
                key={a.value}
                value={a.value}
                sx={{ "&.Mui-selected": { backgroundColor: "#2e7d32", color: "white", "&:hover": { backgroundColor: "#256428" } } }}
              >
                {a.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={hours}
            exclusive
            onChange={(e, value) => value !== null && setHours(value)}
            size="small"
          >
            {DURATIONS.map((d) => (
              <ToggleButton
                key={d.value}
                value={d.value}
                sx={{ "&.Mui-selected": { backgroundColor: "#4caf50", color: "white", "&:hover": { backgroundColor: "#43a047" } } }}
              >
                {d.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Button
            variant="contained"
            sx={{ backgroundColor: "#4caf50", color: "white" }}
            startIcon={<Add />}
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? "Generating…" : "Generate Link"}
          </Button>
        </Box>

        {newLink && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#f1f8f2", border: "1px solid #4caf50", borderRadius: 1, padding: 1.5, mb: 2, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ wordBreak: "break-all", flex: 1 }}>
              {newLink}
            </Typography>
            <Button size="small" startIcon={<ContentCopy />} onClick={() => handleCopy(newLink, "new")}>
              {copiedId === "new" ? "Copied!" : "Copy"}
            </Button>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#4caf50" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Link</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>For</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Created By</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Created</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Expires</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.length > 0 ? (
                invites.map((invite, index) => (
                  <TableRow key={invite._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ wordBreak: "break-all" }}>
                      {invite.link}
                    </TableCell>
                    <TableCell>{invite.audience === "returning" ? "Existing Student" : "New Applicant"}</TableCell>
                    <TableCell>{inviteType(invite)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={invite.status} color={STATUS_COLOR[invite.status] || "default"} />
                    </TableCell>
                    <TableCell>{invite.createdByName || "—"}</TableCell>
                    <TableCell>{new Date(invite.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{invite.expiresAt ? new Date(invite.expiresAt).toLocaleString() : "One time (until submitted)"}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        disabled={invite.status !== "active"}
                        onClick={() => handleCopy(invite.link, invite._id)}
                      >
                        {copiedId === invite._id ? "Copied!" : "Copy"}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingId === invite._id}
                        onClick={() => handleDelete(invite._id)}
                        title="Delete link"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No links generated yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default ScholarshipInviteLinks;
